import { version } from '../../../../package.json';
import { createAsyncTrack } from '../../../services/asyncTrack';
import { createCrossTabBroadcast } from '../../../services/crossTabBroadcast/createCrossTabBroadcast';
import { createEventEmitter } from '../../../services/eventEmitter';
import { createWebFetch } from '../../../services/fetch/createWebFetch';
import { createLogger } from '../../../services/logger/createLogger';
import { createNavigationHandler } from '../../../services/navigate/createNavigationHandler';
import { createWebDeeplinkOpener } from '../../../services/openDeeplink/createWebDeeplinkOpener';
import { createWebPasskeyService } from '../../../services/passkey';
import { createRuntimeServices } from '../../../services/runtimeServices';
import {
  createLocalStorageAdapter,
  createStorage,
} from '../../../services/storage';
import { createDebouncedMutex } from '../../../utils/debouncedMutex/createDebouncedMutex';
import { isServerSideRendering } from '../../../utils/isServerSideRendering/isServerSideRendering';
import { createObservableState } from '../../../utils/observable/createObservableState';
import { DEFAULT_API_BASE_URL } from '../../consts';
import type { DynamicClientConfig } from '../../types';
import type { DynamicCore, DynamicCoreState } from '../types';
import { getInitialState } from './getInitialState';

/**
 * Creates a core instance that contains all the services and state of the Dynamic SDK client.
 */
export const createCore = (config: DynamicClientConfig): DynamicCore => {
  const apiBaseUrl = config.coreConfig?.apiBaseUrl ?? DEFAULT_API_BASE_URL;

  const logger =
    config.coreConfig?.logger ?? createLogger({ level: config.logLevel });

  const storage = createStorage({
    prefix: `dynamic_${config.environmentId}`,
    storageAdapter:
      config.coreConfig?.storageAdapter ?? createLocalStorageAdapter(),
  });

  const fetch = config.coreConfig?.fetch ?? createWebFetch();

  const openDeeplink =
    config.coreConfig?.openDeeplink ?? createWebDeeplinkOpener();

  const navigate = config.coreConfig?.navigate ?? createNavigationHandler();

  const state = createObservableState<DynamicCoreState>(getInitialState);

  const debouncedMutex = createDebouncedMutex();

  const eventEmitter = createEventEmitter();

  const initTrack = createAsyncTrack();

  const runtimeServices = createRuntimeServices();

  const passkey = config.coreConfig?.passkey ?? createWebPasskeyService();

  const deviceSigner = config.coreConfig?.deviceSigner;

  const crossTabBroadcast =
    config.coreConfig?.crossTabBroadcast ??
    createCrossTabBroadcast({
      channelName: `dynamic_${config.environmentId}_broadcast`,
    });

  return {
    apiBaseUrl,
    crossTabBroadcast,
    debouncedMutex,
    deviceSigner,
    environmentId: config.environmentId,
    eventEmitter,
    extensions: new Set(),
    fetch,
    getApiHeaders: config.coreConfig?.getApiHeaders ?? (() => ({})),
    initTrack,
    logger,
    metadata: {
      ...config.metadata,
      universalLink:
        config.metadata?.universalLink ??
        // eslint-disable-next-line no-restricted-globals
        (isServerSideRendering() ? undefined : window.location.origin),
    },
    navigate,
    openDeeplink,
    passkey,
    runtimeServices,
    state,
    storage,
    transformers: config.transformers,
    version,
  };
};
